import type { Chapter } from '@quranjs/api';
import { ClientOnly, Link } from '@tanstack/react-router';
import chaptersJson from '@/data/chapters.json';
import partToFirstPage from '@/data/part-to-first-page-id.json';
import { usePageReaderContext } from '@/features/reader/hooks/use-page-reader-context';
import { useLayoutMode } from '@/features/reader/hooks/use-layout-mode';
import { useOnlineStatus } from '@/features/offline/hooks/use-online-status';

const chapters = chaptersJson as unknown as Chapter[];
import { READER_PAGES_COUNT } from '@/shared/constants/constants';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/breadcrumb';

function OfflineChip() {
  const isOnline = useOnlineStatus();
  if (isOnline) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
      Offline
    </span>
  );
}

export function PageBreadcrumb() {
  const { versesByChapter, pageId } = usePageReaderContext();
  const { layoutMode } = useLayoutMode();

  const chapterIds = Object.keys(versesByChapter);
  const firstVerse = Object.values(versesByChapter)[0]?.[0];
  const juzNumber = firstVerse?.juzNumber ?? 1;

  const surahLabel = chapterIds
    .map((id) => chapters.find((c) => c.id === Number(id))?.nameSimple)
    .filter(Boolean)
    .join(' / ');

  const firstChapter = chapters.find((c) => c.id === Number(chapterIds[0]));
  const surahStartPage = firstChapter?.pages[0] ?? 1;

  const juzStartPage =
    partToFirstPage[String(juzNumber) as keyof typeof partToFirstPage];

  return (
    <Breadcrumb className="flex justify-center pt-[max(env(safe-area-inset-top),1rem)] shrink-0 transition-transform duration-300 ease-out group-[[data-chrome-hidden]]/chrome:-translate-y-full group-[[data-chrome-hidden]]/chrome:pointer-events-none">
      <BreadcrumbList className="text-sm text-muted-foreground gap-1.5 flex-nowrap bg-background/80 backdrop-blur-xl border border-border/30 shadow-sm shadow-black/5 rounded-full px-3 py-1">
        <ClientOnly fallback={null}>
          <OfflineChip />
        </ClientOnly>
        <BreadcrumbItem>
          <BreadcrumbLink
            asChild
            className="no-underline hover:text-foreground/90"
          >
            <Link to="/page/$id" params={{ id: String(juzStartPage) }}>
              Juz {juzNumber}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbSeparator className="text-muted-foreground text-xl leading-none">
          &middot;
        </BreadcrumbSeparator>

        <BreadcrumbItem className="truncate max-w-[60vw]">
          <BreadcrumbLink
            asChild
            className="no-underline hover:text-foreground/90"
          >
            <Link to="/page/$id" params={{ id: String(surahStartPage) }}>
              {surahLabel}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbSeparator className="text-muted-foreground text-xl leading-none">
          &middot;
        </BreadcrumbSeparator>

        <BreadcrumbItem>
          <BreadcrumbPage className="text-foreground font-medium">
            {layoutMode === 'spread'
              ? (() => {
                  const num = Number(pageId);
                  const spreadStart = num % 2 === 1 ? num : num - 1;
                  const spreadEnd = Math.min(
                    spreadStart + 1,
                    READER_PAGES_COUNT,
                  );
                  return spreadStart === spreadEnd
                    ? `p.${spreadStart}`
                    : `p.${spreadStart}\u2013${spreadEnd}`;
                })()
              : `p.${pageId}`}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
